// frontend/src/components/StaffSelectionModal.jsx
// Supabase対応版 - 複数選択・指導対応・既存配置初期表示
// 🔧 スクロール修正版: モーダル全体のレイアウトを flex-col で整理、見切れ対策

import React, { useState, useEffect, useMemo } from 'react';
import { X, Users, UserCheck, GraduationCap, AlertCircle } from 'lucide-react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import dayjs from 'dayjs';
import StaffHistoryModal from './StaffHistoryModal';
import InstructionRankingPanel from './InstructionRankingPanel';
import { fetchOrgDayShiftMarks } from '../utils/dayShiftPatterns';

// スキルレベル順序
const SKILL_ORDER = ["未経験","見学済み","経験1回","経験2回","経験3回以上","外から見守り","独り立ち","指導可"];
const INDEPENDENT_LEVELS = ["独り立ち", "指導可"];

// 指導関係の役割（手術内のロールではなく、指導関係上の立場）
const INSTRUCTION_ROLE_LABEL = {
  normal: '通常',
  instructor: '指導者',
  trainee: '育成対象',
};
const INSTRUCTION_ROLE_BADGE_CLASS = {
  normal:     'bg-gray-200 text-gray-700',
  instructor: 'bg-purple-200 text-purple-800',
  trainee:    'bg-orange-200 text-orange-800',
};

const StaffSelectionModal = ({ isOpen, surgery, selectedDate, onClose, onAssign }) => {
  const { currentOrganization } = useOrganization();

  const [showRankingPanel, setShowRankingPanel] = useState(false); // 🔥 ランキングパネル開閉
  const [staffList, setStaffList] = useState([]);
  const [skillRecords, setSkillRecords] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [busyStaff, setBusyStaff] = useState({});
  const [loading, setLoading] = useState(true);
  const [dayShiftStaffIds, setDayShiftStaffIds] = useState([]);
  // 🔥 日次役割で「手術配置から除外」設定された人員の staff.id Set
  const [excludedByDailyRole, setExcludedByDailyRole] = useState(new Set());

  // 同部屋に配置できない相性ペア: { staffId: Set<otherStaffId> }
  const [sameRoomIncompatPairs, setSameRoomIncompatPairs] = useState({});

  const [activeTab, setActiveTab] = useState({ scrub: 'independent', circulating: 'independent' });

  const [selectedStaff, setSelectedStaff] = useState({ scrub: [], circulating: [] });

  const [showExcludePanel, setShowExcludePanel] = useState(false);
  const [savingExclude, setSavingExclude] = useState(false);
  const [excludeSearch, setExcludeSearch] = useState('');
  const [excludeYearFilter, setExcludeYearFilter] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [historyModal, setHistoryModal] = useState(null);
  const [assignError, setAssignError] = useState('');

  // ==========================================
  // データ読み込み
  // ==========================================
  // 背景スクロールロック
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !surgery || !currentOrganization?.id) return;

    const fetchAllSkillRecords = async () => {
      const PAGE_SIZE = 1000;
      let allRecords = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('skill_records')
          .select('user_id, procedure_id, role, level')
          .eq('organization_id', currentOrganization?.id)
          .range(from, from + PAGE_SIZE - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRecords = allRecords.concat(data);
        if (data.length < PAGE_SIZE) break;
        from += PAGE_SIZE;
      }
      return { data: allRecords, error: null };
    };

    const loadData = async () => {
      setLoading(true);
      try {
        const [
          { data: staffData, error: staffError },
          { data: skillData, error: skillError },
          { data: procData, error: procError },
          { data: incompatData, error: incompatError },
        ] = await Promise.all([
          supabase.from('staff').select('*').eq('organization_id', currentOrganization?.id).eq('is_active', true),
          fetchAllSkillRecords(),
          supabase.from('procedures').select('*').eq('organization_id', currentOrganization?.id),
          supabase.from('shift_incompatibilities')
            .select('user_a_staff_id, user_b_staff_id, scope')
            .eq('organization_id', currentOrganization?.id)
            .eq('scope', 'sameRoom'),
        ]);

        if (staffError) throw staffError;
        if (skillError) throw skillError;
        if (procError) throw procError;
        if (incompatError) console.warn('相性ルール取得エラー（継続）:', incompatError.message);

        // sameRoom 相性ペアを双方向 map に変換
        const pairs = {};
        (incompatData || []).forEach(r => {
          if (!r.user_a_staff_id || !r.user_b_staff_id) return;
          if (!pairs[r.user_a_staff_id]) pairs[r.user_a_staff_id] = new Set();
          if (!pairs[r.user_b_staff_id]) pairs[r.user_b_staff_id] = new Set();
          pairs[r.user_a_staff_id].add(r.user_b_staff_id);
          pairs[r.user_b_staff_id].add(r.user_a_staff_id);
        });
        setSameRoomIncompatPairs(pairs);

        const staff = (staffData || []).map(s => ({
          ...s,
          employeeId: s.employee_id,
          lastName: s.last_name,
          firstName: s.first_name,
          name: `${s.last_name || ''} ${s.first_name || ''}`.trim(),
        }));

        const procs = (procData || []).map(p => ({ ...p, departmentId: p.department_id }));

        setStaffList(staff);
        setSkillRecords(skillData || []);
        setProcedures(procs);

        await Promise.all([
          loadBusyStaff(),
          loadDayShiftStaff(surgery.date || selectedDate, staff),
          loadExcludedByDailyRole(surgery.date || selectedDate),
        ]);

        initializeSelectedStaff(staff, skillData || []);
      } catch (error) {
        console.error('❌ データ読み込みエラー:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, surgery, currentOrganization?.id]);

  // ==========================================
  // 既存配置の初期選択
  // ==========================================
  const initializeSelectedStaff = (staff, skills) => {
    const initial = { scrub: [], circulating: [] };

    const toArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      return [val];
    };

    ['scrub', 'circulating'].forEach(role => {
      const existing = toArray(surgery[role]);
      existing.forEach(assigned => {
        if (!assigned?.employeeId) return;
        const found = staff.find(s => String(s.employee_id) === String(assigned.employeeId));
        if (found) {
          // 保存済みの instructionRole があれば復元、なければ 'normal'
          initial[role].push({
            ...found,
            instructionRole: assigned.instructionRole || 'normal',
          });
        }
      });
    });

    setSelectedStaff(initial);
  };

  // ==========================================
  // 手術中スタッフ取得（時間重複チェック対応）
  // busyStaff: { employeeId, surgeries: [{procedure, start, end}] } の配列
  // ==========================================
  const loadBusyStaff = async () => {
    try {
      const dateStr = surgery.date || selectedDate;
      const thisStart = surgery.start || surgery.start_time;
      const thisEnd   = surgery.end   || surgery.end_time;

      const { data, error } = await supabase
        .from('surgery_schedules')
        .select('id, scrub, circulating, procedure, start_time, end_time')
        .eq('organization_id', currentOrganization?.id)
        .eq('date', dateStr);
      if (error) throw error;

      // 時間重複判定: 他手術と1分でも重なるか
      const isOverlap = (s, e) => {
        if (!thisStart || !thisEnd || !s || !e) return false;
        // "HH:MM" 文字列を分に変換
        const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
        return toMin(s) < toMin(thisEnd) && toMin(e) > toMin(thisStart);
      };

      // employeeId → [{procedure, start, end}] のマップ
      const busyMap = {};
      (data || []).forEach(s => {
        if (s.id === surgery.docId) return; // 自分自身はスキップ
        const overlaps = isOverlap(s.start_time, s.end_time);
        const info = { procedure: s.procedure || '手術', start: s.start_time, end: s.end_time, overlaps };

        const addBusy = (val) => {
          if (!val) return;
          const arr = Array.isArray(val) ? val : [val];
          arr.forEach(v => {
            if (!v?.employeeId) return;
            const empId = v.employeeId;
            if (!busyMap[empId]) busyMap[empId] = [];
            busyMap[empId].push(info);
          });
        };
        addBusy(s.scrub);
        addBusy(s.circulating);
      });

      setBusyStaff(busyMap);
    } catch (error) {
      console.error('❌ 手術中スタッフ取得エラー:', error);
    }
  };

  // ==========================================
  // 日勤者取得（Phase F: パターンベース・インクルーシブ判定）
  // ==========================================
  // 旧仕様の「空 shift_type = 日勤」は廃止。
  // shift_patterns.configuration.assignmentModeEnabled = true のパターンの short_name と
  // monthly_shifts.shift_type が一致するスタッフだけを日勤者として扱う。
  // 🔥 日次役割で「手術配置から除外」設定された人員を取得
  //   その日の daily_role_assignments × daily_role_definitions.exclude_from_surgery を見て、
  //   除外対象の staff.id を Set にまとめる。
  const loadExcludedByDailyRole = async (surgeryDate) => {
    try {
      if (!currentOrganization?.id || !surgeryDate) return;
      const dateStr = dayjs(surgeryDate).format('YYYY-MM-DD');
      const { data: roles } = await supabase
        .from('daily_role_definitions')
        .select('id, exclude_from_surgery')
        .eq('organization_id', currentOrganization.id)
        .eq('exclude_from_surgery', true);
      const excludeRoleIds = (roles || []).map((r) => r.id);
      if (excludeRoleIds.length === 0) {
        setExcludedByDailyRole(new Set());
        return;
      }
      const { data: asgs } = await supabase
        .from('daily_role_assignments')
        .select('staff_ids')
        .eq('organization_id', currentOrganization.id)
        .eq('assignment_date', dateStr)
        .in('role_definition_id', excludeRoleIds);
      const set = new Set();
      (asgs || []).forEach((a) => {
        (Array.isArray(a.staff_ids) ? a.staff_ids : []).forEach((sid) => set.add(sid));
      });
      setExcludedByDailyRole(set);
    } catch (e) {
      console.warn('日次役割の除外取得失敗:', e?.message);
      setExcludedByDailyRole(new Set());
    }
  };

  const loadDayShiftStaff = async (surgeryDate, staffData) => {
    try {
      const dateStr = dayjs(surgeryDate).format('YYYY-MM-DD');
      const dayShiftMarks = await fetchOrgDayShiftMarks(currentOrganization?.id);
      if (!dayShiftMarks || dayShiftMarks.size === 0) {
        console.warn('⚠️ 「人員配置モードで使用する」とマークされた勤務パターンが未設定です');
        setDayShiftStaffIds([]);
        return;
      }
      const { data, error } = await supabase
        .from('monthly_shifts')
        .select('employee_id, shift_type')
        .eq('organization_id', currentOrganization?.id)
        .eq('shift_date', dateStr);
      if (error) throw error;
      const dayShiftEmployeeIds = new Set(
        (data || [])
          .filter((row) => row.shift_type && dayShiftMarks.has(String(row.shift_type).trim()))
          .map((row) => String(row.employee_id))
      );
      const dayShiftIds = staffData
        .filter((s) => dayShiftEmployeeIds.has(String(s.employee_id)))
        .map((s) => s.id);
      setDayShiftStaffIds(dayShiftIds);
    } catch (error) {
      console.error('❌ 日勤者取得エラー:', error);
      setDayShiftStaffIds([]);
    }
  };

  // ==========================================
  // 術式IDの取得
  // ==========================================
  const procedureIds = useMemo(() => {
    if (!surgery?.requiredSkills || surgery.requiredSkills.length === 0) return [];
    if (typeof surgery.requiredSkills[0] === 'object' && surgery.requiredSkills[0].id) {
      return surgery.requiredSkills.map(s => s.id).filter(Boolean);
    }
    return surgery.requiredSkills.map(skillName => {
      const proc = procedures.find(p => p.name === skillName);
      return proc?.id || null;
    }).filter(Boolean);
  }, [surgery, procedures]);

  // ==========================================
  // スキルレベル取得
  // ==========================================
  const getStaffSkillLevel = (staff, role) => {
    if (!procedureIds.length) return "未経験";
    const condition = surgery.skillCondition || 'AND';
    const levels = procedureIds.map(procId => {
      const record = skillRecords.find(r => r.user_id === staff.id && r.procedure_id === procId && r.role === role);
      return record?.level || "未経験";
    });
    if (condition === 'AND') {
      return levels.reduce((min, level) => SKILL_ORDER.indexOf(level) < SKILL_ORDER.indexOf(min) ? level : min);
    } else {
      return levels.reduce((max, level) => SKILL_ORDER.indexOf(level) > SKILL_ORDER.indexOf(max) ? level : max);
    }
  };

  // ==========================================
  // 同部屋相性ルールによる除外対象 staffId 集合
  // 既に選択中のスタッフ（scrub + circulating）の sameRoom 相性ペアを集める。
  // 既選択者自身は対象外（自分を弾かない）。
  // ==========================================
  const sameRoomBlockedIds = useMemo(() => {
    const assignedIds = new Set();
    selectedStaff.scrub.forEach(s => assignedIds.add(s.id));
    selectedStaff.circulating.forEach(s => assignedIds.add(s.id));
    const blocked = new Set();
    assignedIds.forEach(id => {
      const conflicts = sameRoomIncompatPairs[id];
      if (conflicts) conflicts.forEach(cid => blocked.add(cid));
    });
    // 既に選択されている本人は除外しない
    assignedIds.forEach(id => blocked.delete(id));
    return blocked;
  }, [selectedStaff, sameRoomIncompatPairs]);

  // ==========================================
  // スタッフフィルタリング
  // ==========================================
  const getFilteredStaff = (role) => {
    // 🔥 同じ手術内で「他 role に既に選択されているスタッフ」を候補から除外する。
    //   例: scrub に既に山田さんが居る → circulating の候補から山田さんを除外。
    //   役職重複登録バグを UI レイヤーで予防する (保存時にも防御済み)。
    const otherRole = role === 'scrub' ? 'circulating' : 'scrub';
    const otherSelectedIds = new Set((selectedStaff[otherRole] || []).map((s) => s.id));

    let filtered = staffList.filter(s => {
      if (s.is_excluded) return false;
      if (sameRoomBlockedIds.has(s.id)) return false;  // 同部屋相性ルールで除外
      if (otherSelectedIds.has(s.id)) return false;     // 🔥 他 role に既選択 → 重複防止で除外
      if (excludedByDailyRole.has(s.id)) return false;  // 🔥 日次役割で「手術除外」フラグが立った人
      if (dayShiftStaffIds.length > 0 && !dayShiftStaffIds.includes(s.id)) return false;
      if (searchKeyword.trim()) {
        const kw = searchKeyword.trim().toLowerCase();
        const name = `${s.last_name || ''}${s.first_name || ''}`.toLowerCase();
        const nameSpaced = `${s.last_name || ''} ${s.first_name || ''}`.toLowerCase();
        const year = String(s.year || '');
        const expYear = s.year ? String(new Date().getFullYear() - parseInt(s.year) + 1) : '';
        if (!name.includes(kw) && !nameSpaced.includes(kw) && !year.includes(kw) && !expYear.includes(kw)) return false;
      }
      return true;
    });

    const withSkills = filtered.map(staff => {
      const busyInfo = busyStaff[staff.employee_id] || [];
      const overlapBusy = busyInfo.filter(b => b.overlaps);   // 時間重複あり
      const nonOverlapBusy = busyInfo.filter(b => !b.overlaps); // 時間重複なし（別の手術に配置済みだが時間外）
      return {
        ...staff,
        skillLevel: getStaffSkillLevel(staff, role),
        isBusy: overlapBusy.length > 0,        // 時間重複ありのみ「手術中」扱い
        overlapBusy,                            // 重複手術リスト
        nonOverlapBusy,                         // 別枠配置済み手術リスト
      };
    });
    withSkills.sort((a, b) => {
      // 時間重複あり → 末尾
      if (a.isBusy !== b.isBusy) return a.isBusy ? 1 : -1;
      // 別枠配置済み → 少し後ろ
      if ((a.nonOverlapBusy.length > 0) !== (b.nonOverlapBusy.length > 0)) return a.nonOverlapBusy.length > 0 ? 1 : -1;
      return SKILL_ORDER.indexOf(b.skillLevel) - SKILL_ORDER.indexOf(a.skillLevel);
    });
    return withSkills;
  };

  const scrubStaff = useMemo(() => {
    const all = getFilteredStaff('scrub');
    // 必要術式が未設定の場合：スキルレベルでの絞り込みは行わず全員表示
    if (procedureIds.length === 0) return all;
    if (activeTab.scrub === 'independent') return all.filter(s => INDEPENDENT_LEVELS.includes(s.skillLevel));
    return all.filter(s => !INDEPENDENT_LEVELS.includes(s.skillLevel));
    // 🔥 selectedStaff.circulating を依存に追加: circulating で選択が変わったら scrub 候補も更新
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList, skillRecords, procedureIds, activeTab.scrub, busyStaff, dayShiftStaffIds, searchKeyword, sameRoomBlockedIds, excludedByDailyRole, selectedStaff.circulating]);

  const circulatingStaff = useMemo(() => {
    const all = getFilteredStaff('circulating');
    if (procedureIds.length === 0) return all;
    if (activeTab.circulating === 'independent') return all.filter(s => INDEPENDENT_LEVELS.includes(s.skillLevel));
    return all.filter(s => !INDEPENDENT_LEVELS.includes(s.skillLevel));
    // 🔥 selectedStaff.scrub を依存に追加: scrub で選択が変わったら circulating 候補も更新
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList, skillRecords, procedureIds, activeTab.circulating, busyStaff, dayShiftStaffIds, searchKeyword, sameRoomBlockedIds, excludedByDailyRole, selectedStaff.scrub]);

  // ==========================================
  // スタッフ選択（複数対応・トグル）
  // ==========================================
  const handleStaffSelect = (role, staff) => {
    setAssignError('');
    setSelectedStaff(prev => {
      const current = prev[role];
      const isSelected = current.some(s => s.id === staff.id);
      if (isSelected) {
        return { ...prev, [role]: current.filter(s => s.id !== staff.id) };
      } else {
        // 新規追加時は指導関係上の役割を 'normal' で初期化
        return { ...prev, [role]: [...current, { ...staff, instructionRole: 'normal' }] };
      }
    });
  };

  // 一覧から「通常 / 指導者 / 育成対象」のいずれかのボタンで配置を直接指定
  // ・同じ役割で再クリック → 解除
  // ・別の役割をクリック → 役割を切替
  // ・未配置からクリック → その役割で新規追加
  const selectStaffWithRole = (role, staff, instructionRole) => {
    setAssignError('');
    setSelectedStaff(prev => {
      const current = prev[role];
      const existing = current.find(s => s.id === staff.id);
      if (existing && existing.instructionRole === instructionRole) {
        return { ...prev, [role]: current.filter(s => s.id !== staff.id) };
      }
      if (existing) {
        return { ...prev, [role]: current.map(s =>
          s.id === staff.id ? { ...s, instructionRole } : s
        )};
      }
      return { ...prev, [role]: [...current, { ...staff, instructionRole }] };
    });
  };

  // ==========================================
  // バリデーション
  // ==========================================
  const validateAssignment = () => {
    const scrubList = selectedStaff.scrub;
    const circList = selectedStaff.circulating;

    // 両方空 = 配置解除として許可（既存配置を外したい場合）

    const scrubHasIndependent = scrubList.some(s => {
      const level = getStaffSkillLevel(s, 'scrub');
      return INDEPENDENT_LEVELS.includes(level);
    });
    const circHasIndependent = circList.some(s => {
      const level = getStaffSkillLevel(s, 'circulating');
      return INDEPENDENT_LEVELS.includes(level);
    });

    if (scrubList.length > 0 && !scrubHasIndependent) {
      setAssignError('器械出しに自立者（独り立ち・指導可）が1人もいません。指導なしの育成者のみの配置はできません。');
      return false;
    }
    if (circList.length > 0 && !circHasIndependent) {
      setAssignError('外回りに自立者（独り立ち・指導可）が1人もいません。指導なしの育成者のみの配置はできません。');
      return false;
    }

    return true;
  };

  // ==========================================
  // 配置確定
  // ==========================================
  const handleAssign = () => {
    if (!validateAssignment()) return;

    const assignments = {};

    // 両方空 = 配置を完全に外す
    if (selectedStaff.scrub.length === 0 && selectedStaff.circulating.length === 0) {
      onAssign({ scrub: null, circulating: null });
      onClose();
      return;
    }

    // 時間重複スタッフがいるか確認してwarningメッセージを生成
    const buildOverlapWarning = (staffArr) => {
      const overlapNames = staffArr
        .filter(s => (busyStaff[s.employee_id] || []).some(b => b.overlaps))
        .map(s => {
          const info = (busyStaff[s.employee_id] || []).find(b => b.overlaps);
          return `${s.name}（${info?.procedure} ${info?.start}〜${info?.end}）`;
        });
      return overlapNames.length > 0
        ? `⚠️ 時間重複あり: ${overlapNames.join('、')}`
        : null;
    };

    if (selectedStaff.scrub.length > 0) {
      const independent = selectedStaff.scrub.filter(s => INDEPENDENT_LEVELS.includes(getStaffSkillLevel(s, 'scrub')));
      const trainees = selectedStaff.scrub.filter(s => !INDEPENDENT_LEVELS.includes(getStaffSkillLevel(s, 'scrub')));
      const primary = independent[0];
      assignments.scrub = {
        staff: primary,
        type: 'jiritsu',
        level: primary.skillLevel || getStaffSkillLevel(primary, 'scrub'),
        count: 0,
        allStaff: selectedStaff.scrub,
        trainees,
        overlapWarning: buildOverlapWarning(selectedStaff.scrub),
      };
    }

    if (selectedStaff.circulating.length > 0) {
      const independent = selectedStaff.circulating.filter(s => INDEPENDENT_LEVELS.includes(getStaffSkillLevel(s, 'circulating')));
      const trainees = selectedStaff.circulating.filter(s => !INDEPENDENT_LEVELS.includes(getStaffSkillLevel(s, 'circulating')));
      const primary = independent[0];
      assignments.circulating = {
        staff: primary,
        type: 'jiritsu',
        level: primary.skillLevel || getStaffSkillLevel(primary, 'circulating'),
        count: 0,
        allStaff: selectedStaff.circulating,
        trainees,
        overlapWarning: buildOverlapWarning(selectedStaff.circulating),
      };
    }

    onAssign(assignments);
    onClose();
  };

  // ==========================================
  // 配置対象外トグル
  // ==========================================
  const toggleExcludeStaff = async (staff) => {
    const newValue = !staff.is_excluded;
    setSavingExclude(true);
    try {
      const { error } = await supabase.from('staff').update({ is_excluded: newValue }).eq('id', staff.id);
      if (error) throw error;
      setStaffList(prev => prev.map(s => s.id === staff.id ? { ...s, is_excluded: newValue } : s));
    } catch (e) {
      console.error('対象外設定エラー:', e);
    } finally {
      setSavingExclude(false);
    }
  };

  if (!isOpen) return null;

  const scrubNames = selectedStaff.scrub.map(s => s.name).join('、');
  const circNames = selectedStaff.circulating.map(s => s.name).join('、');
  const hasSelection = selectedStaff.scrub.length > 0 || selectedStaff.circulating.length > 0;

  return (
    <>
      {historyModal && (
        <StaffHistoryModal
          staff={historyModal.staff}
          role={historyModal.role}
          onClose={() => setHistoryModal(null)}
          isSelected={selectedStaff[historyModal.role]?.some(s => s.id === historyModal.staff.id)}
          onSelect={(staff) => {
            handleStaffSelect(historyModal.role, staff);
            setHistoryModal(null);
          }}
        />
      )}
      {!historyModal && (
        // 🔧 修正: モーダル全体を画面内固定。モーダル内のスタッフリストのみスクロール
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{
          padding:'60px 16px 80px',
          background: 'radial-gradient(circle at 20% 20%, rgba(59,130,246,0.15), transparent 50%), radial-gradient(circle at 80% 80%, rgba(16,185,129,0.15), transparent 50%), rgba(15,23,42,0.55)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}>
          <div className="w-full max-w-3xl flex flex-col" style={{height:'100%', maxHeight:'100%', overflow:'hidden'}}>
            <div className="w-full flex flex-col" style={{
              height:'100%', overflow:'hidden',
              borderRadius: '24px',
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '1px solid rgba(255,255,255,0.7)',
              boxShadow: '0 24px 64px -16px rgba(15,23,42,0.35), 0 0 0 1px rgba(255,255,255,0.4) inset',
            }}>

              {/* ヘッダー: 全情報を2カラムグリッドでコンパクトに */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 flex-shrink-0 rounded-t-lg">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold leading-tight">人員配置</h3>
                    <p className="text-xs text-blue-100 truncate">{surgery.procedure} · {surgery.start}〜{surgery.end}</p>
                  </div>
                  <button onClick={onClose} className="text-white hover:text-gray-200 ml-3 flex-shrink-0">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="pt-2 border-t border-blue-400 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  {/* 左列 */}
                  <div className="space-y-1">
                    <div className="flex items-start gap-1">
                      <span className="text-blue-200 flex-shrink-0 w-12">術式</span>
                      <div className="flex flex-wrap gap-1">
                        {surgery.requiredSkills && surgery.requiredSkills.length > 0
                          ? surgery.requiredSkills.map((skill, idx) => (
                              <span key={idx} className="px-1.5 py-0.5 bg-blue-500 rounded">
                                {typeof skill === 'object' ? skill.name : skill}
                              </span>
                            ))
                          : <span className="text-blue-200">未設定</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-blue-200 flex-shrink-0 w-12">診療科</span>
                      <span>{surgery.department || <span className="text-blue-200">未設定</span>}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-blue-200 flex-shrink-0 w-12">執刀医</span>
                      <span>{surgery.surgeon || <span className="text-blue-200">未設定</span>}</span>
                    </div>
                  </div>
                  {/* 右列 */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <span className="text-blue-200 flex-shrink-0 w-12">体位</span>
                      <span>{surgery.position || <span className="text-blue-200">未設定</span>}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-blue-200 flex-shrink-0 w-12">麻酔</span>
                      <span>{surgery.anesthesia || <span className="text-blue-200">未設定</span>}</span>
                    </div>
                    {surgery.tags && surgery.tags.length > 0 && (
                      <div className="flex items-start gap-1">
                        <span className="text-blue-200 flex-shrink-0 w-12">タグ</span>
                        <div className="flex flex-wrap gap-1">
                          {surgery.tags.map((tag, idx) => (
                            <span key={idx} className="px-1.5 py-0.5 bg-indigo-500 rounded">{tag.name || tag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 選択中スタッフ 固定バー（常時表示・×で解除可能） */}
              <div className="bg-gray-50 border-b px-4 py-2 flex-shrink-0 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs font-medium text-blue-600 flex-shrink-0">器械出し:</span>
                    {selectedStaff.scrub.length > 0 ? (
                      <div className="flex gap-1 flex-wrap">
                        {selectedStaff.scrub.map(s => {
                          const role = s.instructionRole || 'normal';
                          return (
                            <span key={s.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              {s.name}
                              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${INSTRUCTION_ROLE_BADGE_CLASS[role]}`}>
                                {INSTRUCTION_ROLE_LABEL[role]}
                              </span>
                              <button onClick={() => handleStaffSelect('scrub', s)} className="text-blue-400 hover:text-blue-700 leading-none">×</button>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">未選択</span>
                    )}
                  </div>
                  <div className="w-px h-4 bg-gray-300 flex-shrink-0 hidden sm:block" />
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs font-medium text-green-600 flex-shrink-0">外回り:</span>
                    {selectedStaff.circulating.length > 0 ? (
                      <div className="flex gap-1 flex-wrap">
                        {selectedStaff.circulating.map(s => {
                          const role = s.instructionRole || 'normal';
                          return (
                            <span key={s.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {s.name}
                              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${INSTRUCTION_ROLE_BADGE_CLASS[role]}`}>
                                {INSTRUCTION_ROLE_LABEL[role]}
                              </span>
                              <button onClick={() => handleStaffSelect('circulating', s)} className="text-green-400 hover:text-green-700 leading-none">×</button>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">未選択</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRankingPanel((v) => !v)}
                  className="flex-shrink-0 px-3 py-1.5 rounded-md text-sm font-medium transition-colors bg-amber-100 text-amber-800 hover:bg-amber-200"
                  title="この術式の指導者ランキング"
                >
                  📊 ランキング
                </button>
                <button
                  onClick={handleAssign}
                  className="flex-shrink-0 px-4 py-1.5 rounded-md text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
                >
                  配置確定
                </button>
              </div>

              {/* 🔥 ランキング展開エリア */}
              {showRankingPanel && (
                <div className="border-b border-gray-200 px-4 py-3 bg-amber-50">
                  {procedureIds.length === 0 ? (
                    <div className="text-xs text-gray-500">術式が設定されていないためランキング表示できません</div>
                  ) : (
                    <InstructionRankingPanel
                      filterProcedureId={procedureIds[0]}
                      defaultPeriod="6m"
                      compact={true}
                    />
                  )}
                </div>
              )}

              {/* コンテンツ */}
              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">スタッフ情報を読み込み中...</p>
                </div>
              ) : (
                <div className="flex flex-col overflow-y-auto flex-1">
                  {/* 検索バー・注意書き */}
                  <div className="px-4 pt-4 pb-0 flex-shrink-0">
                    <input
                      type="text"
                      value={searchKeyword}
                      onChange={e => setSearchKeyword(e.target.value)}
                      placeholder="名前・年次で検索..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-3"
                    />
                    {procedureIds.length === 0 && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                        <p className="text-sm text-yellow-800 font-medium">必要術式が設定されていないため、全スタッフを表示しています</p>
                      </div>
                    )}
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                      💡 複数人を選択できます。指導配置の場合は自立者（指導可・独り立ち）＋育成者を選択してください。育成者のみの配置は確定できません。
                    </div>
                    {sameRoomBlockedIds.size > 0 && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>
                          相性ルール（同じ部屋に配置しない）により <strong>{sameRoomBlockedIds.size} 名</strong> を候補から除外しています。
                          解除する場合は、選択中のスタッフを外してください。
                        </span>
                      </div>
                    )}
                  </div>

                  {/* 人員リスト */}
                  <div className="px-4 pb-2">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <RoleSection
                        role="scrub" title="器械出し" color="blue"
                        activeTab={activeTab.scrub}
                        onTabChange={(tab) => setActiveTab(prev => ({ ...prev, scrub: tab }))}
                        staff={scrubStaff}
                        selectedStaff={selectedStaff.scrub}
                        onSelectWithRole={(staff, instructionRole) => selectStaffWithRole('scrub', staff, instructionRole)}
                        onHistoryClick={(staff) => setHistoryModal({ staff, role: 'scrub' })}
                        getSkillLevel={(s) => getStaffSkillLevel(s, 'scrub')}
                      />
                      <RoleSection
                        role="circulating" title="外回り" color="green"
                        activeTab={activeTab.circulating}
                        onTabChange={(tab) => setActiveTab(prev => ({ ...prev, circulating: tab }))}
                        staff={circulatingStaff}
                        selectedStaff={selectedStaff.circulating}
                        onSelectWithRole={(staff, instructionRole) => selectStaffWithRole('circulating', staff, instructionRole)}
                        onHistoryClick={(staff) => setHistoryModal({ staff, role: 'circulating' })}
                        getSkillLevel={(s) => getStaffSkillLevel(s, 'circulating')}
                      />
                    </div>
                  </div>

                  {/* 配置対象外パネル */}
                  {showExcludePanel && (() => {
                    const now = new Date();
                    const currentFiscalYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
                    const yearToExp = (y) => y ? currentFiscalYear - Number(y) + 1 : null;
                    const dayShiftAll = staffList
                      .filter(s => dayShiftStaffIds.length === 0 || dayShiftStaffIds.includes(s.id))
                      .sort((a, b) => `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`, 'ja'));
                    const availableYears = [...new Set(dayShiftAll.map(s => s.year).filter(Boolean))].sort((a, b) => Number(b) - Number(a));
                    const filtered = dayShiftAll.filter(s => {
                      const fullName = `${s.last_name || ''}${s.first_name || ''}`;
                      return (excludeSearch === '' || fullName.includes(excludeSearch))
                        && (excludeYearFilter === '' || String(s.year) === excludeYearFilter);
                    });
                    const excludedCount = dayShiftAll.filter(s => s.is_excluded).length;
                    return (
                      <div className="border-t bg-orange-50 px-6 py-4 flex-shrink-0">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-bold text-orange-800">配置対象外スタッフの設定</h4>
                          <span className="text-xs text-orange-600">チェックした人は人員配置リストに表示されません</span>
                        </div>
                        <div className="flex gap-2 mb-2">
                          <input type="text" value={excludeSearch} onChange={e => setExcludeSearch(e.target.value)} placeholder="名前で検索..." className="flex-1 px-3 py-1.5 text-sm border border-orange-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                          <select value={excludeYearFilter} onChange={e => setExcludeYearFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-orange-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                            <option value="">全学年</option>
                            {availableYears.map(y => <option key={y} value={String(y)}>{yearToExp(y)}年目</option>)}
                          </select>
                          {(excludeSearch || excludeYearFilter) && (
                            <button onClick={() => { setExcludeSearch(''); setExcludeYearFilter(''); }} className="px-2 text-sm text-gray-500 hover:text-gray-700 border border-orange-200 rounded-md bg-white">✕</button>
                          )}
                        </div>
                        <div className="text-xs text-orange-500 mb-2">
                          {filtered.length}名表示中（日勤者{dayShiftAll.length}名中）
                          {excludedCount > 0 && <span className="ml-2 font-medium">対象外: {excludedCount}名</span>}
                        </div>
                        <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto pr-1">
                          {filtered.length === 0 ? (
                            <div className="col-span-3 text-center text-gray-400 text-sm py-3">該当なし</div>
                          ) : filtered.map(s => (
                            <label key={s.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${s.is_excluded ? 'bg-orange-100 border-orange-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                              <input type="checkbox" checked={!!s.is_excluded} onChange={() => toggleExcludeStaff(s)} disabled={savingExclude} className="accent-orange-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className={`text-sm truncate ${s.is_excluded ? 'text-orange-700 font-medium' : 'text-gray-700'}`}>{s.last_name} {s.first_name}</div>
                                {s.year && <div className="text-xs text-gray-400">{yearToExp(s.year)}年目</div>}
                              </div>
                            </label>
                          ))}
                        </div>
                        {savingExclude && <p className="text-xs text-orange-400 mt-1">保存中...</p>}
                      </div>
                    );
                  })()}

                  {/* エラー表示 */}
                  {assignError && (
                    <div className="mx-6 mb-2 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2 flex-shrink-0">
                      <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-700 font-medium">{assignError}</p>
                    </div>
                  )}

                  {/* フッター: 対象外設定＋キャンセルのみ（配置確定は上部バーに常時表示） */}
                  <div className="border-t bg-gray-50 px-4 py-3 flex-shrink-0 rounded-b-lg flex items-center justify-between gap-3">
                    <button
                      onClick={() => setShowExcludePanel(v => !v)}
                      className={`px-3 py-1.5 text-sm rounded-md border font-medium transition-colors ${showExcludePanel ? 'bg-orange-100 border-orange-400 text-orange-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                    >
                      ⚠️ 対象外設定
                    </button>
                    <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                      キャンセル
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==========================================
// 役割セクション
// ==========================================
const RoleSection = ({ role, title, color, activeTab, onTabChange, staff, selectedStaff, onSelectWithRole, onHistoryClick, getSkillLevel }) => {
  // 🌊 Liquid Glass (Glassmorphism) パレット:
  //   - 半透明 + backdrop-blur で「すりガラス」感
  //   - 内側ハイライト (inset shadow) + 外側ソフト影
  //   - グラデーション背景でコンテンツの背景が透ける
  const colorClasses = {
    blue: {
      headerGrad: 'bg-gradient-to-br from-blue-500/15 via-sky-400/10 to-cyan-300/15',
      borderTint: 'border-blue-200/60',
      textTitle:  'text-blue-900',
      selectedCard: 'ring-2 ring-blue-400/60 bg-blue-50/60 backdrop-blur-md',
      badge: 'bg-blue-600',
      tabActive:   'bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-[0_4px_16px_-4px_rgba(37,99,235,0.5),inset_0_1px_0_rgba(255,255,255,0.3)]',
      listGrad:    'bg-gradient-to-b from-blue-50/40 to-white/30',
    },
    green: {
      headerGrad: 'bg-gradient-to-br from-emerald-500/15 via-green-400/10 to-teal-300/15',
      borderTint: 'border-emerald-200/60',
      textTitle:  'text-emerald-900',
      selectedCard: 'ring-2 ring-emerald-400/60 bg-emerald-50/60 backdrop-blur-md',
      badge: 'bg-emerald-600',
      tabActive:   'bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-[0_4px_16px_-4px_rgba(16,185,129,0.5),inset_0_1px_0_rgba(255,255,255,0.3)]',
      listGrad:    'bg-gradient-to-b from-emerald-50/40 to-white/30',
    },
  };
  const colors = colorClasses[color];
  const isTrainee = activeTab === 'trainee';
  const traineeTabActive = 'bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-[0_4px_16px_-4px_rgba(249,115,22,0.5),inset_0_1px_0_rgba(255,255,255,0.3)]';
  const traineeListGrad = 'bg-gradient-to-b from-orange-50/40 to-white/30';
  const currentListGrad = isTrainee ? traineeListGrad : colors.listGrad;
  const tabInactive = 'bg-white/40 backdrop-blur-sm text-gray-600 hover:bg-white/70 hover:text-gray-900 border border-white/50';

  return (
    <div
      className={`rounded-2xl overflow-hidden border ${colors.borderTint} bg-white/40 backdrop-blur-xl
        shadow-[0_8px_32px_-8px_rgba(15,23,42,0.15),inset_0_1px_0_rgba(255,255,255,0.6)]`}
    >
      <div className={`${colors.headerGrad} backdrop-blur-md px-4 py-3 border-b ${colors.borderTint}`}>
        <h4 className={`font-bold tracking-tight ${colors.textTitle}`}>{title}</h4>
      </div>
      <div className="flex gap-2 p-2 bg-white/20 backdrop-blur-md border-b border-white/40">
        {[['independent', <UserCheck className="h-4 w-4 mr-1" />, '自立者'], ['trainee', <GraduationCap className="h-4 w-4 mr-1" />, '育成者']].map(([tab, icon, label]) => {
          const isActive = activeTab === tab;
          const activeClass = tab === 'trainee' ? traineeTabActive : colors.tabActive;
          return (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${isActive ? activeClass : tabInactive}`}
            >
              <div className="flex items-center justify-center">{icon}{label}</div>
            </button>
          );
        })}
      </div>
      <div className={`p-4 space-y-2 overflow-y-auto ${currentListGrad} backdrop-blur-sm`} style={{maxHeight:'45vh'}}>
        {staff.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>該当するスタッフがいません</p>
          </div>
        ) : (
          staff.map(s => {
            const isSelected = selectedStaff.some(sel => sel.id === s.id);
            const level = getSkillLevel(s);
            const isIndependent = INDEPENDENT_LEVELS.includes(level);

            // 時間重複ありは薄く表示、別枠配置済みはやや薄く
            // 🌊 Glassmorphism: 半透明背景 + ホバーで浮き上がる
            const cardClass = s.isBusy
              ? 'bg-red-50/70 border-red-200/60 opacity-60 backdrop-blur-sm'
              : s.nonOverlapBusy?.length > 0
                ? 'bg-yellow-50/70 border-yellow-200/60 backdrop-blur-sm'
                : isSelected
                  ? colors.selectedCard
                  : 'bg-white/60 border-white/70 backdrop-blur-sm hover:bg-white/85 hover:border-white hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.15)] hover:-translate-y-0.5';

            return (
              <div key={s.id} className={`p-3 border rounded-xl transition-all duration-200 ${cardClass}`}>
                <div className="flex justify-between items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{s.name}</span>
                      {isIndependent ? (
                        <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">自立</span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">育成</span>
                      )}
                      {/* 時間重複あり */}
                      {s.isBusy && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                          ⚠️ 手術中 ({s.overlapBusy[0]?.start}〜{s.overlapBusy[0]?.end})
                        </span>
                      )}
                      {/* 別枠配置済み（時間重複なし） */}
                      {!s.isBusy && s.nonOverlapBusy?.length > 0 && (
                        <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-medium">
                          配置済 ({s.nonOverlapBusy[0]?.start}〜{s.nonOverlapBusy[0]?.end})
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{level}</div>
                  </div>
                  {/* 役割を直接選んで配置（通常 / 指導者 / 育成対象） */}
                  <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                    {(['normal', 'instructor', 'trainee']).map((roleKey) => {
                      const currentRole = selectedStaff.find(sel => sel.id === s.id)?.instructionRole;
                      const active = isSelected && currentRole === roleKey;
                      const activeClass = {
                        normal:     'bg-gray-600 text-white border-transparent',
                        instructor: 'bg-purple-600 text-white border-transparent',
                        trainee:    'bg-orange-500 text-white border-transparent',
                      }[roleKey];
                      const idleClass = s.isBusy
                        ? 'border-red-200 text-red-600 hover:bg-red-50'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-100';
                      return (
                        <button
                          key={roleKey}
                          onClick={() => onSelectWithRole(s, roleKey)}
                          className={`text-[11px] px-2 py-1 rounded-md font-medium border transition-colors ${active ? activeClass : idleClass}`}
                          title={`${INSTRUCTION_ROLE_LABEL[roleKey]}として配置${active ? '中（再度押すと解除）' : ''}`}
                        >
                          {active && '✓ '}{INSTRUCTION_ROLE_LABEL[roleKey]}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => onHistoryClick(s)}
                      className="text-[11px] px-2 py-1 rounded-md font-medium border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                      詳細
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StaffSelectionModal;